import React, { useEffect, useState } from "react";
import { useT } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { isNative } from "../lib/native";
import {
    AppleLogo, AndroidLogo, ShareNetwork, PlusSquare,
    DotsThreeVertical, CheckCircle, ArrowUpRight, DeviceMobile,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";


// Anonymous funnel event emitter — fire-and-forget, never block the page on it.
function emitInstallEvent(kind, platform, source) {
    try {
        const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/install-event`;
        const sessionId = (() => {
            try {
                let s = sessionStorage.getItem("zcardon_install_sid");
                if (!s) { s = Math.random().toString(36).slice(2, 12); sessionStorage.setItem("zcardon_install_sid", s); }
                return s;
            } catch { return ""; }
        })();
        const payload = JSON.stringify({ kind, platform, source, session_id: sessionId });
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
        } else {
            fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => {});
        }
    } catch { /* swallow */ }
}

/**
 * Mobile-install landing page — what the homepage QR actually points at.
 *
 * The previous flow dropped scanners on the marketing homepage and hoped
 * the browser would fire `beforeinstallprompt`. That never works on iOS
 * (Apple disables it) and only sometimes works on Android (Chrome's
 * engagement heuristic blocks it for first-time visitors). Result: the
 * user scanned, landed, and nothing happened.
 *
 * This page does the only thing that actually works:
 *   1. Detect platform on mount.
 *   2. iOS  → big visual "Tap Share → Add to Home Screen" with an arrow
 *             pointing at Safari's bottom toolbar.
 *   3. Android → if `beforeinstallprompt` fired, render a single big
 *                Install button. Otherwise show the manual menu route.
 *   4. Already running as the installed PWA → green confirmation card.
 *   5. Webview (Instagram/Facebook/TikTok in-app browser) → tells the user
 *      to open in Safari/Chrome and copies the URL.
 */
function isIos() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const iOSUa = /iPad|iPhone|iPod/.test(ua);
    const iPadOS = /Mac/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
    return iOSUa || iPadOS;
}
function isAndroid() {
    if (typeof navigator === "undefined") return false;
    return /Android/i.test(navigator.userAgent || "");
}
function isStandalone() {
    if (typeof window === "undefined") return false;
    return Boolean(window.navigator.standalone) || window.matchMedia("(display-mode: standalone)").matches;
}
function isWebview() {
    if (typeof navigator === "undefined") return false;
    return /(FBAN|FBAV|Instagram|Line|Twitter|TikTok|MicroMessenger)/i.test(navigator.userAgent || "");
}

export default function Install() {
    const t = useT();
    const [installEvent, setInstallEvent] = useState(null);
    const [justInstalled, setJustInstalled] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const onBefore = (e) => {
            e.preventDefault();
            setInstallEvent(e);
        };
        const onInstalled = () => {
            setJustInstalled(true);
            setInstallEvent(null);
            // Funnel event: completed (Android only — iOS doesn't fire `appinstalled`)
            const platform = isIos() ? "ios" : isAndroid() ? "android" : "desktop";
            emitInstallEvent("install_completed", platform, source);
        };
        window.addEventListener("beforeinstallprompt", onBefore);
        window.addEventListener("appinstalled", onInstalled);
        // Funnel event: view (one per session — derive platform/source on mount)
        const platform = isWebview() ? "webview" : isIos() ? "ios" : isAndroid() ? "android" : "desktop";
        const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
        const source = params.get("from") || "direct";
        emitInstallEvent("install_view", platform, source);
        // Stash source so the `appinstalled` handler above can attach it.
        window.__zcardon_install_source = source;
        return () => {
            window.removeEventListener("beforeinstallprompt", onBefore);
            window.removeEventListener("appinstalled", onInstalled);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const platform = isIos() ? "ios" : isAndroid() ? "android" : "desktop";
    const inWebview = isWebview();
    const alreadyInstalled = isNative() || isStandalone();

    const url = typeof window !== "undefined" ? window.location.origin : "";
    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked */ }
    };

    return (
        <div className="min-h-screen bg-[#FBF9F6] text-[#0A1F16] flex flex-col">
            <header className="px-6 pt-8 pb-4">
                <p className="serif text-2xl">✦ Zcardon</p>
            </header>

            <main className="flex-1 px-5 sm:px-8 max-w-lg mx-auto w-full pb-12">
                <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A5C50] mt-2">
                    {t("install.eyebrow") || "Install Zcardon"}
                </p>
                <h1 className="serif text-3xl sm:text-4xl mt-2 leading-tight">
                    {t("install.title") || "Add Zcardon to your home screen"}
                </h1>
                <p className="text-sm text-[#4A5C50] mt-3 leading-relaxed">
                    {t("install.subtitle") || "Two taps and you'll have Zcardon as a real app icon — no App Store needed."}
                </p>

                {alreadyInstalled ? (
                    <AlreadyInstalled t={t} />
                ) : inWebview ? (
                    <WebviewWarning t={t} url={url} copyUrl={copyUrl} copied={copied} />
                ) : justInstalled ? (
                    <JustInstalled t={t} />
                ) : platform === "ios" ? (
                    <IosFlow t={t} />
                ) : platform === "android" ? (
                    <AndroidFlow
                        t={t}
                        installEvent={installEvent}
                        setInstallEvent={setInstallEvent}
                    />
                ) : (
                    <DesktopFallback t={t} url={url} copyUrl={copyUrl} copied={copied} />
                )}

                <div className="mt-10 pt-6 border-t border-[#E5E0D8]">
                    <a
                        href="/"
                        className="inline-flex items-center gap-1.5 text-sm text-[#4A5C50] hover:text-[#0A1F16]"
                        data-testid="install-back-home"
                    >
                        ← {t("install.back_home") || "Open Zcardon in browser"}
                    </a>
                </div>
            </main>
        </div>
    );
}


function IosFlow({ t }) {
    return (
        <div className="mt-8" data-testid="install-ios-flow">
            <p className="text-[12px] text-[#4A5C50] bg-[#184A30]/[0.04] border border-[#184A30]/15 rounded-lg px-3 py-2.5 mb-5">
                {t("install.steps_hint") || "These steps happen in Safari, not on this page. None of the rows below are tappable — they're just describing what to look for."}
            </p>
            <ol className="space-y-5">
                <Step
                    n="1"
                    Icon={AppleLogo}
                    title={t("install.ios_step1_title") || "Open this page in Safari"}
                    body={t("install.ios_step1_body") || "If you scanned from another browser, copy the URL and open it in Safari first."}
                />
                <Step
                    n="2"
                    Icon={ShareNetwork}
                    title={t("install.ios_step2_title") || "Tap the Share button"}
                    body={t("install.ios_step2_body") || "It's the square with an arrow pointing up — at the bottom of Safari (or top on iPad)."}
                />
                <Step
                    n="3"
                    Icon={PlusSquare}
                    title={t("install.ios_step3_title") || "Tap \"Add to Home Screen\""}
                    body={t("install.ios_step3_body") || "Then confirm. Zcardon appears as an app icon — tap it to launch full-screen."}
                />
            </ol>

            {/* Animated arrow pointing at Safari's bottom toolbar */}
            <div className="mt-8 relative flex justify-center" aria-hidden="true">
                <div className="text-center">
                    <p className="text-xs text-[#4A5C50] mb-2">{t("install.ios_arrow_label") || "Look for this icon"}</p>
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#184A30] text-white shadow-lg animate-bounce">
                        <ShareNetwork size={24} weight="bold" />
                    </div>
                </div>
            </div>
        </div>
    );
}


function AndroidFlow({ t, installEvent, setInstallEvent }) {
    const [busy, setBusy] = useState(false);

    const trigger = async () => {
        if (!installEvent) return;
        setBusy(true);
        try {
            await installEvent.prompt();
            await installEvent.userChoice;
        } catch { /* user cancelled */ }
        setInstallEvent(null);
        setBusy(false);
    };

    if (installEvent) {
        return (
            <div className="mt-8" data-testid="install-android-prompt">
                <div className="rounded-2xl bg-white border border-[#E5E0D8] p-6 text-center">
                    <AndroidLogo size={44} weight="duotone" className="text-[#184A30] mx-auto" />
                    <p className="font-semibold text-lg mt-3">{t("install.android_ready_title") || "Ready to install"}</p>
                    <p className="text-sm text-[#4A5C50] mt-2">
                        {t("install.android_ready_body") || "Chrome detected Zcardon as installable. Tap below and we'll appear as an app icon."}
                    </p>
                    <Button
                        type="button"
                        onClick={trigger}
                        disabled={busy}
                        className="mt-6 w-full h-12 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
                        data-testid="install-android-button"
                    >
                        {busy ? (t("install.android_installing") || "Installing…") : (t("install.android_install") || "Install Zcardon")}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-8" data-testid="install-android-flow">
            <p className="text-[12px] text-[#4A5C50] bg-[#184A30]/[0.04] border border-[#184A30]/15 rounded-lg px-3 py-2.5 mb-5">
                {t("install.steps_hint") || "These steps happen in Chrome, not on this page. None of the rows below are tappable — they're describing what to look for in Chrome's menu."}
            </p>
            <ol className="space-y-5">
                <Step
                    n="1"
                    Icon={DotsThreeVertical}
                    title={t("install.android_step1_title") || "Tap Chrome's menu"}
                    body={t("install.android_step1_body") || "It's the three dots in the top-right corner of Chrome."}
                />
                <Step
                    n="2"
                    Icon={PlusSquare}
                    title={t("install.android_step2_title") || "Tap \"Install app\" or \"Add to Home screen\""}
                    body={t("install.android_step2_body") || "Confirm to add the Zcardon icon to your phone. Tap it any time to launch."}
                />
            </ol>
            <div className="mt-6 rounded-xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-4 text-sm text-[#8C6B21]" data-testid="install-android-hint">
                {t("install.android_hint") || "If you don't see \"Install app\" yet, browse the home page once — Chrome unlocks it after a brief visit."}
            </div>
        </div>
    );
}


function DesktopFallback({ t, url, copyUrl, copied }) {
    return (
        <div className="mt-8" data-testid="install-desktop-fallback">
            <div className="rounded-2xl bg-white border border-[#E5E0D8] p-6">
                <div className="flex items-start gap-3">
                    <DeviceMobile size={36} weight="duotone" className="text-[#184A30] shrink-0" />
                    <div className="min-w-0">
                        <p className="font-semibold">{t("install.desktop_title") || "Scan with your phone"}</p>
                        <p className="text-sm text-[#4A5C50] mt-1.5 leading-relaxed">
                            {t("install.desktop_body_qr") || "Zcardon installs as an app icon on iPhone or Android — not on desktop. Point your phone camera at this QR code:"}
                        </p>
                    </div>
                </div>
                <div className="mt-5 flex justify-center p-5 rounded-2xl bg-[#FBF9F6] border border-[#E5E0D8]" data-testid="install-desktop-qr">
                    <QRCodeSVG
                        value={url ? `${url.replace(/\/$/, "")}/install?from=desktop-qr` : ""}
                        size={180}
                        fgColor="#0A1F16"
                        bgColor="#FBF9F6"
                        includeMargin={false}
                    />
                </div>
                <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-[#4A5C50] text-center">
                    {t("install.desktop_or") || "Or copy the link"}
                </p>
                <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 mono text-[11px] text-[#4A5C50] bg-[#FBF9F6] border border-[#E5E0D8] rounded-lg px-3 py-2 break-all">
                        {url}
                    </code>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={copyUrl}
                        className="rounded-lg border-[#184A30] text-[#184A30] hover:bg-[#184A30]/5 shrink-0"
                        data-testid="install-copy-url"
                    >
                        {copied ? (t("install.copied") || "Copied") : (t("install.copy") || "Copy")}
                    </Button>
                </div>
            </div>
        </div>
    );
}


function WebviewWarning({ t, url, copyUrl, copied }) {
    return (
        <div className="mt-8" data-testid="install-webview-warning">
            <div className="rounded-2xl bg-white border-2 border-[#CBA35C] p-6">
                <ArrowUpRight size={36} weight="duotone" className="text-[#CBA35C]" />
                <p className="font-semibold mt-3">{t("install.webview_title") || "Open in your browser"}</p>
                <p className="text-sm text-[#4A5C50] mt-2 leading-relaxed">
                    {t("install.webview_body") || "Apps like Instagram, Facebook, and TikTok use a built-in browser that can't install other apps. Open this link in Safari or Chrome:"}
                </p>
                <div className="mt-4 flex items-center gap-2">
                    <code className="flex-1 mono text-[11px] text-[#4A5C50] bg-[#FBF9F6] border border-[#E5E0D8] rounded-lg px-3 py-2 break-all">
                        {url}
                    </code>
                    <Button
                        size="sm"
                        onClick={copyUrl}
                        className="rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-white shrink-0"
                        data-testid="install-copy-url"
                    >
                        {copied ? (t("install.copied") || "Copied") : (t("install.copy") || "Copy link")}
                    </Button>
                </div>
            </div>
        </div>
    );
}


function AlreadyInstalled({ t }) {
    return (
        <div className="mt-8 rounded-2xl bg-[#184A30]/10 border border-[#184A30]/20 p-6 text-center" data-testid="install-already">
            <CheckCircle size={40} weight="duotone" className="text-[#184A30] mx-auto" />
            <p className="font-semibold mt-3">{t("install.already_title") || "You're already in the app"}</p>
            <p className="text-sm text-[#4A5C50] mt-2">
                {t("install.already_body") || "Zcardon is installed on this device. Tap the icon on your home screen to open it any time."}
            </p>
            <a
                href="/"
                className="inline-block mt-4 text-sm font-medium text-[#184A30] hover:underline"
                data-testid="install-already-continue"
            >
                {t("install.continue") || "Continue to Zcardon →"}
            </a>
        </div>
    );
}


function JustInstalled({ t }) {
    return (
        <div className="mt-8 rounded-2xl bg-[#184A30]/10 border border-[#184A30]/20 p-6 text-center" data-testid="install-just-done">
            <CheckCircle size={40} weight="duotone" className="text-[#184A30] mx-auto" />
            <p className="font-semibold mt-3">{t("install.installed_title") || "Installed"}</p>
            <p className="text-sm text-[#4A5C50] mt-2">
                {t("install.installed_body") || "Look for the Zcardon icon on your home screen — that's your app."}
            </p>
        </div>
    );
}


function Step({ n, Icon, title, body }) {
    return (
        <li className="flex gap-4" data-testid={`install-step-${n}`}>
            <div className="shrink-0 w-10 h-10 rounded-xl bg-[#184A30] text-white flex items-center justify-center font-semibold">
                {n}
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-semibold flex items-center gap-2">
                    <Icon size={18} weight="duotone" className="text-[#184A30]" />
                    {title}
                </p>
                <p className="mt-1 text-[13px] text-[#4A5C50] leading-relaxed">{body}</p>
            </div>
        </li>
    );
}
